// DMTreeGridSummaryLazy.tsx
import React, { Component } from 'react';
import { Badge, Button, Form, Modal, Nav, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faTimesCircle, faPen, faPlus,
  faCircleInfo, faWandMagicSparkles, faGavel,
  faFilter, faChevronDown, faChevronRight,
  faExclamationTriangle, faShieldHalved, faSpinner, faProjectDiagram,
  faDownload, faCaretDown, faFileExcel, faFileCode, faFileCsv, faDatabase
  
} from '@fortawesome/free-solid-svg-icons';

import { convertToRawDm, DQ_RULE_CAT_LABELS, toTitleCaseLabel } from '../constants';
import RuleDetailsModal from './DQRuleDetailsModal';
import {
  adriva_dq_postAction,
  getDQSummary, getSamples, getDMProfile, getDMProfilePage, LAZY_CONFIG,
  type DQLifecycleSummary, type SampleData, type DMProfile, type DMProfilePage,
} from '../API';
import DQRuleStudio from '../dq/DQRuleStudio';   // adjust path to where you place it
import type { GeneratedRule } from '../dqAPI';
import PolicyDocDashboard, { SourceFilter, PolicyDashboardSummary } from './PolicyDocDashboard';
import { T } from '../theme/theme';
import CollapsibleSection from './CollapsibleSection';
import { deriveKind, ENGINEER_KINDS } from '../dq/cleansingKind';
import CleansingPatternDashboard, {
  CleansingFilters, EMPTY_CLEANSING_FILTERS, CleansingDashboardSummary,
} from '../dq/CleansingPatternDashboard';
import { DQRulesReport } from '../dq/DQCrossSubEntityRules'; 
import { getCrossDQSummary, downloadDQRulesExport,
        type RuleLifecycleFilter, type RuleExportFormat, type SqlDialect } from '../dqAPI';

// ─── CSS (unchanged) ──────────────────────────────────────────────────────────
const GLOBAL_STYLE = `
  .dq-root { --brand:${T.brand}; --brandLight:${T.brandLight}; --brandMid:${T.brandMid};
    --border:${T.border}; --bg:${T.bg}; --bgCard:${T.bgCard}; --bgSidebar:${T.bgSidebar};
    --textPrimary:${T.textPrimary}; --textSecond:${T.textSecond}; --textMuted:${T.textMuted};
    --green:${T.green}; --greenBg:${T.greenBg}; --greenBorder:${T.greenBorder};
    --amber:${T.amber}; --amberBg:${T.amberBg}; --amberBorder:${T.amberBorder};
    --red:${T.red}; --redBg:${T.redBg}; --redBorder:${T.redBorder};
    --purple:${T.purple}; --purpleBg:${T.purpleBg}; --purpleBorder:${T.purpleBorder};
    font-family:'Inter var','Inter',system-ui,sans-serif; color:var(--textPrimary);
  }
  .dq-card { background:var(--bgCard); border-radius:12px; border:1px solid var(--border); box-shadow:0 1px 3px rgba(0,0,0,.06); }
  .dq-pill { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:20px; font-size:13px; font-weight:600; cursor:pointer; transition:all .15s; border:1.5px solid transparent; user-select:none; }
  .dq-pill.active { color:#fff; }
  .dq-pill.inactive { background:var(--bgCard); color:var(--textMuted); border-color:var(--border); }
  .dq-pill.inactive:hover { border-color:var(--brandMid); color:var(--brand); }
  .dq-tab { padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; white-space:nowrap; border:none; background:transparent; color:var(--textMuted); transition:all .15s; }
  .dq-tab:hover { background:var(--brandLight); color:var(--brand); }
  .dq-tab.active { background:var(--brand); color:#fff; }
  .dq-table { width:100%; border-collapse:collapse; }
  .dq-table thead th { background:var(--bgSidebar); color:var(--textLabel); font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; padding:10px 16px; border-bottom:1.5px solid var(--border); position:sticky; top:0; z-index:2; }
  .dq-table tbody tr { border-bottom:1px solid var(--border); }
  .dq-table tbody tr:last-child { border-bottom:none; }
  .dq-table tbody tr:hover > td { background:rgba(30,58,110,.03); }
  .dq-table tbody td { padding:14px 16px; vertical-align:top; }
  .rule-row { display:flex; align-items:flex-start; gap:10px; padding:8px 10px; border-radius:8px; transition:background .12s; cursor:default; }
  .rule-row:hover { background:var(--bgSidebar); }
  .lc-badge { font-size:10px; font-weight:700; padding:2px 8px; border-radius:10px; text-transform:uppercase; letter-spacing:.04em; }
  .lc-Approved { background:var(--greenBg); color:var(--green); border:1px solid var(--greenBorder); }
  .lc-Draft    { background:var(--bgSidebar); color:var(--textMuted); border:1px solid var(--border); }
  .lc-Rejected { background:var(--redBg); color:var(--red); border:1px solid var(--redBorder); }
  .lc-Waiting  { background:var(--amberBg); color:var(--amber); border:1px solid var(--amberBorder); }
  .micro-bar { height:6px; border-radius:3px; overflow:hidden; background:var(--bgSidebar); }
  .micro-bar-fill { height:100%; border-radius:3px; }
  .stacked-bar { height:8px; border-radius:4px; overflow:hidden; display:flex; }
  .dq-select { appearance:none; background:var(--bgCard); border:1px solid var(--border); border-radius:8px; font-size:12px; color:var(--textPrimary); padding:5px 28px 5px 10px; cursor:pointer; outline:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23445566'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 8px center; transition:border-color .15s; }
  .dq-select:focus,.dq-select:hover { border-color:var(--brandMid); }
  .add-rule-btn { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; color:var(--brand); background:var(--brandLight); border:1.5px solid var(--brandMid); border-radius:20px; padding:4px 12px; cursor:pointer; transition:all .15s; white-space:nowrap; }
  .add-rule-btn:hover { background:var(--brand); color:#fff; border-color:var(--brand); }
  .cat-chip { font-size:11px; font-weight:700; padding:2px 10px; border-radius:10px; background:var(--purpleBg); color:var(--purple); border:1px solid var(--purpleBorder); }
  .vbar-wrap { display:flex; align-items:flex-end; gap:3px; height:52px; }
  .vbar-item { width:9px; border-radius:2px 2px 0 0; min-height:2px; }
  .group-hdr { background:var(--bgSidebar); }
  .group-hdr td { padding:10px 16px; }
  .group-toggle { display:inline-flex; align-items:center; gap:8px; font-size:13px; font-weight:700; color:var(--brand); cursor:pointer; border:none; background:none; padding:0; }
  .rules-cat-hdr { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:var(--textMuted); padding:4px 0 6px; }
  .stat-label { font-size:10px; font-weight:700; color:var(--textMuted); text-transform:uppercase; letter-spacing:.06em; margin-top:4px; }
  ::-webkit-scrollbar { width:6px; height:6px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:var(--border); border-radius:3px; }
`;

function exportBtnStyle(color: string, bg: string, border: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 11, fontWeight: 600, color,
    background: bg, border: `1px solid ${border}`,
    borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
  };
}
// ─── Types ────────────────────────────────────────────────────────────────────

// Props — df and dv removed (self-fetched now), entityId + appNm added
type Props = {
  selectedApp:      any;
  tool:             string;
  dm:               any;
  entityId:         string;   // NEW — entity id for self-fetch
  appNm:            string;   // NEW — app name for context
  onEditFieldClick?: (field: any) => void;
};

type RuleFilter = 'ALL' | 'Approved' | 'Draft' | 'Waiting' | 'Rejected';

type State = {
  // self-fetch
  dqSummary:         DQLifecycleSummary | null;
  summaryLoading:    boolean;

  // DM + profile (+ rules) loaded server-side, paginated PER SUB-ENTITY.
  // One call (getDMProfile) returns the first page of every sub-entity with
  // profile already attached per field; "Load more" pulls the next page for a
  // single sub-entity (getDMProfilePage). No client-side join.
  dmLoading:         boolean;
  entityLabel:       string;
  subMeta:           Record<string, { total: number; loaded: number; hasMore: boolean; loading: boolean; isRoot: boolean; label: string }>;

  tokenMap:          any | null;
  samplesCache:      Record<string, any>;             // key = source_prefix
  samplesLoading:    Record<string, boolean>;

  // UI
  viewMode:          'tree' | 'tabs';
  activeGroup:       string;
  expandedGroups:    Set<string>;
  ruleStatusFilter:  RuleFilter;
  catFilter:         string;
  dimFilter:         string;
  columnSearch:      string;
  selectedField:     any | null;
  selectedRule:      any | null;
  // Rule Studio (Add / Tune) — replaces the old chat tuning assistant
  studio:            null | {
    mode:         'add' | 'tune';
    sourcePrefix: string;
    column:       string;        // orig_column_name
    columnLabel?: string;
    existingRule?: any | null;
  };
  sourceFilter:      SourceFilter;
  cleansingFilters:  CleansingFilters;
  expandedDashboard: 'cleansing' | 'policy' | null;
  hideEmptyFields:   boolean;
  searchAllGroups:   boolean;
  showCrossEntity:   boolean;
  exportMenuOpen:    boolean;
  exporting:         boolean;
  sqlDialect:        SqlDialect;
  dmLocal:           any;

  crossEntityRuleCount: number | null;
  
};

// ─── Helpers (unchanged) ─────────────────────────────────────────────────────
const LC_ICON: Record<string, any> = {
  Approved: faCheckCircle, Draft: faPen, Rejected: faTimesCircle, Waiting: faCircleInfo,
};
function resolveLifecycle(lc?: string): string {
  return (!lc || lc.trim() === '') ? 'Draft' : lc;
}
const CAT_LABELS: Record<string, string> = {
  std: 'Standardization', val: 'Validation', cons: 'Consistency', ref: 'Reference', mgmt: 'Governance',
};
const DIM_COLOR: Record<string, string> = {
  Completeness: '#166534', Accuracy: '#1e3a6e', Consistency: '#6b21a8',
  Validity: '#7c2d00', Uniqueness: '#0f766e', Timeliness: '#0369a1',
};

function getBlocksForField(tokenMap: any, field: any): Record<string, any> {
  const srcKey   = field?.profile?.source_prefix;
  const origName = field?.profile?.orig_column_name;
  const colName  = field?.profile?.column_name;
  if (!tokenMap?.sources || !srcKey) return {};
  const srcMap = tokenMap.sources[srcKey] || {};
  if (origName && srcMap[origName]) return srcMap[origName];
  if (colName  && srcMap[colName])  return srcMap[colName];
  const lo = (origName || '').toLowerCase();
  const lc = (colName  || '').toLowerCase();
  if (lo || lc) {
    for (const key of Object.keys(srcMap)) {
      const lk = key.toLowerCase();
      if ((lo && lk === lo) || (lc && lk === lc)) return srcMap[key];
    }
  }
  return {};
}

function flatLeafFields(fields: any[]): any[] {
  const out: any[] = [];
  for (const f of fields) {
    if (f.eClass === 'FieldGroup') out.push(...flatLeafFields(f.field || []));
    else out.push(f);
  }
  return out;
}

// ─── Mini charts (unchanged) ──────────────────────────────────────────────────
function NullBar({ count, nullCount }: { count: number; nullCount: number }) {
  if (!count) return <span style={{ fontSize: 11, color: T.textMuted }}>—</span>;
  const pct = Math.round((nullCount / count) * 100);
  const okPct = 100 - pct;
  return (
    <div>
      <div className="stacked-bar" style={{ width: 120 }}>
        {pct > 0   && <div style={{ width: `${pct}%`,   background: T.red }} />}
        {okPct > 0 && <div style={{ width: `${okPct}%`, background: '#5f9ea0' }} />}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:T.textMuted, marginTop:2 }}>
        <span>{pct}% null</span><span>{okPct}% ok</span>
      </div>
    </div>
  );
}
const VALUE_TYPE_COLORS: Record<string, string> = {
  int_like:'#0ea5e9', float_like:'#f59e0b', date_like:'#6b7280', boolean_like:'#1e293b', string_like:'#10b981',
};
function ValueTypeBars({ counts }: { counts: Record<string, number> }) {
  const types = ['int_like','float_like','date_like','boolean_like','string_like'];
  const entries = types.map(t => ({ t, n: counts?.[t] || 0 })).filter(e => e.n > 0);
  const total   = entries.reduce((s, e) => s + e.n, 0);
  if (!total) return <span style={{ fontSize:11, color:T.textMuted }}>—</span>;
  return (
    <div className="vbar-wrap">
      {entries.map(({ t, n }) => {
        const pct = (n / total) * 100;
        return (
          <OverlayTrigger key={t} overlay={<Tooltip>{t.replace('_like','')}: {n} ({pct.toFixed(0)}%)</Tooltip>} placement="top">
            <div className="vbar-item" style={{ height:`${pct}%`, background:VALUE_TYPE_COLORS[t] }} />
          </OverlayTrigger>
        );
      })}
    </div>
  );
}
function LengthBars({ dist }: { dist: any[] }) {
  const PALETTE = ['#10b981','#3b82f6','#f59e0b','#8b5cf6','#ef4444','#14b8a6'];
  if (!dist?.length) return <span style={{ fontSize:11, color:T.textMuted }}>—</span>;
  const filtered = dist.filter(d => d.count > 0);
  const total    = filtered.reduce((s, d) => s + d.count, 0);
  if (!total) return <span style={{ fontSize:11, color:T.textMuted }}>—</span>;
  return (
    <div className="vbar-wrap">
      {filtered.map((d, i) => {
        const pct = (d.count / total) * 100;
        return (
          <OverlayTrigger key={i} overlay={<Tooltip>Len {d.length_range}: {d.count} ({pct.toFixed(0)}%)</Tooltip>} placement="top">
            <div className="vbar-item" style={{ height:`${pct}%`, background:PALETTE[i % PALETTE.length] }} />
          </OverlayTrigger>
        );
      })}
    </div>
  );
}
function TopValueBars({ vals, total }: { vals: Record<string, number>; total: number }) {
  const SOFT = ['#6b7280','#3b82f6','#10b981'];
  const sorted = Object.entries(vals || {}).sort((a,b) => b[1]-a[1]).slice(0,3);
  if (!sorted.length) return <span style={{ fontSize:11, color:T.textMuted }}>—</span>;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      {sorted.map(([label, count], i) => {
        const pct = total ? (count / total) * 100 : 0;
        return (
          <OverlayTrigger key={i} overlay={<Tooltip>{label}: {count} ({pct.toFixed(1)}%)</Tooltip>} placement="top">
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:80, fontSize:10, color:T.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</div>
              <div className="micro-bar" style={{ flex:1 }}><div className="micro-bar-fill" style={{ width:`${pct}%`, background:SOFT[i] }} /></div>
              <div style={{ fontSize:10, color:T.textMuted, minWidth:24 }}>{count}</div>
            </div>
          </OverlayTrigger>
        );
      })}
    </div>
  );
}

// ─── Dashboard pill row (unchanged logic) ─────────────────────────────────────
interface DashboardProps {
  counts: Record<string, number>; activeFilter: RuleFilter;
  catFilter: string; dimFilter: string; catCounts: Record<string, number>;
  dimCounts: Record<string, number>; onStatusClick: (f: RuleFilter) => void;
  onCatChange: (v: string) => void; onDimChange: (v: string) => void;
  onReset: () => void; columnSearch: string; onColumnSearch: (v: string) => void;
  totalRules: number; hideEmptyFields: boolean; onToggleHideEmpty: () => void;
  loading: boolean;
}
function DQDashboard(p: DashboardProps) {
  const PILLS: { key: RuleFilter; label: string; color: string; bg: string }[] = [
    { key:'ALL',      label:'All Rules', color:T.brand,     bg:T.brandLight },
    { key:'Approved', label:'Approved',  color:T.green,     bg:T.greenBg },
    { key:'Draft',    label:'Draft',     color:T.textMuted, bg:T.bgSidebar },
    { key:'Rejected', label:'Rejected',  color:T.red,       bg:T.redBg },
  ];
  const isDirty = p.activeFilter !== 'ALL' || p.catFilter || p.dimFilter;
  return (
    <div className="dq-card" style={{ padding:'14px 20px', marginBottom:10, display:'flex', flexWrap:'wrap', gap:12, alignItems:'center', justifyContent:'space-between' }}>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
        <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:T.textMuted, marginRight:4 }}>
          DQ Rules {p.loading && <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft:6, fontSize:10 }} />}
        </span>
        {PILLS.map(pill => {
          const active = p.activeFilter === pill.key;
          return (
            <button key={pill.key} className={`dq-pill ${active ? 'active' : 'inactive'}`}
              style={active ? { background:pill.color, borderColor:pill.color } : {}}
              onClick={() => p.onStatusClick(active ? 'ALL' : pill.key)}>
              {pill.label}
              <span style={{ background:active ? 'rgba(255,255,255,.25)' : T.border, color:active ? '#fff' : T.textMuted, borderRadius:10, padding:'1px 7px', fontSize:11, fontWeight:700 }}>
                {p.counts[pill.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ position:'relative' }}>
          <input type="text" placeholder="Search columns…" value={p.columnSearch} onChange={e => p.onColumnSearch(e.target.value)}
            style={{ border:`1px solid ${T.border}`, borderRadius:8, fontSize:12, padding:'5px 10px 5px 28px', color:T.textPrimary, background:T.bgCard, outline:'none', width:160 }} />
          <FontAwesomeIcon icon={faFilter} style={{ position:'absolute', left:9, top:7, fontSize:11, color:T.textMuted }} />
        </div>
        <select className="dq-select" value={p.catFilter} onChange={e => p.onCatChange(e.target.value)}>
          <option value="">Category: All</option>
          {Object.keys(p.catCounts).sort().map(c => <option key={c} value={c}>{CAT_LABELS[c] || c} ({p.catCounts[c]})</option>)}
        </select>
        <select className="dq-select" value={p.dimFilter} onChange={e => p.onDimChange(e.target.value)}>
          <option value="">Dimension: All</option>
          {Object.keys(p.dimCounts).sort().map(d => <option key={d} value={d}>{d} ({p.dimCounts[d]})</option>)}
        </select>
        {isDirty && (
          <button onClick={p.onReset} style={{ fontSize:12, fontWeight:600, color:T.red, background:T.redBg, border:`1px solid ${T.redBorder}`, borderRadius:8, padding:'5px 12px', cursor:'pointer' }}>Reset</button>
        )}
        <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12, fontWeight:600, color:p.hideEmptyFields ? T.brand : T.textMuted, background:p.hideEmptyFields ? T.brandLight : T.bgCard, border:`1px solid ${p.hideEmptyFields ? T.brandMid : T.border}`, borderRadius:8, padding:'4px 10px', transition:'all .15s', userSelect:'none' as const }}>
          <input type="checkbox" checked={p.hideEmptyFields} onChange={p.onToggleHideEmpty} style={{ accentColor:T.brand, margin:0 }} />
          Matching fields only
        </label>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default class DMTreeGridSummary extends Component<Props, State> {

  // ─── initial state ─────────────────────────────────────────────────────────
  state: State = {
    dqSummary: null, summaryLoading: false,
    dmLoading: false, entityLabel: '', subMeta: {},
    tokenMap: null,
    samplesCache: {}, samplesLoading: {},
    viewMode: 'tabs', activeGroup: '', expandedGroups: new Set(),
    ruleStatusFilter: 'Approved', catFilter: '', dimFilter: '', columnSearch: '',
    selectedField: null, selectedRule: null, studio: null,
    sourceFilter: 'ALL', cleansingFilters: EMPTY_CLEANSING_FILTERS,
    expandedDashboard: null, hideEmptyFields: true, searchAllGroups: false,
    dmLocal: null,
    showCrossEntity: false,
    exportMenuOpen: false,
    exporting: false,
    sqlDialect: 'tsql',
    crossEntityRuleCount: null,
  };

  // ─── lifecycle ─────────────────────────────────────────────────────────────

  componentDidMount() {
    const { dm, entityId } = this.props;

    if (!document.getElementById('dq-global-style')) {
      const s = document.createElement('style');
      s.id = 'dq-global-style';
      s.textContent = GLOBAL_STYLE;
      document.head.appendChild(s);
    }

    // start from a minimal shell; real fields arrive from getDMProfile
    this.setState({ dmLocal: dm ? { ...dm, field: [] } : null });

    if (entityId) {
      this._fetchDQSummary(entityId);
      this._fetchCrossEntityCount(entityId);
      this._fetchTokenMap(entityId);
      this._loadDMProfile(entityId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.entityId !== this.props.entityId) {
      const { dm, entityId } = this.props;
      this.setState({
        dmLocal:        dm ? { ...dm, field: [] } : null,
        dqSummary:      null,
        tokenMap:       null,
        samplesCache:   {},
        samplesLoading: {},
        subMeta:        {},
        dmLoading:      false,
        entityLabel:    '',
        activeGroup:    '',
        expandedGroups: new Set(),
      }, () => {
        if (entityId) {
          this._fetchDQSummary(entityId);
          this._fetchCrossEntityCount(entityId);
          this._fetchTokenMap(entityId);
          this._loadDMProfile(entityId);
        }
      });
    }
  }

  // ─── self-fetch: DQ summary (pill counts) ───────────────────────────────────

  _fetchCrossEntityCount(entityId: string) {
     getCrossDQSummary(entityId)
      .then((res:any) => this.setState({ crossEntityRuleCount: res.cross_table_rule_count }))
       .catch(() => {}); // badge simply doesn't show on error
  }

  _handleExport = (
    lifecycle: RuleLifecycleFilter,
    format: RuleExportFormat,
    dialect?: SqlDialect,
  ) => {
    this.setState({ exporting: true, exportMenuOpen: false });
    downloadDQRulesExport(
      this.props.entityId,
      this.props.appNm,
      lifecycle,
      format,
      dialect ?? this.state.sqlDialect,
    )
      .catch((e) => {
        console.error('[DQ export] failed', e);
        alert('Failed to export DQ rules. Please try again.');
      })
      .finally(() => this.setState({ exporting: false }));
  }

  _fetchDQSummary(entityId: string) {
    if (this.state.summaryLoading || this.state.dqSummary) return;
    this.setState({ summaryLoading: true });
    getDQSummary(entityId)
      .then(res => this.setState({ dqSummary: res, summaryLoading: false }))
      .catch(() => this.setState({ summaryLoading: false }));
  }

  // ─── self-fetch: token map ──────────────────────────────────────────────────

  _fetchTokenMap(entityId: string) {
    if (this.state.tokenMap) return;
    adriva_dq_postAction || void 0; // keep existing import
    import('../API').then(({ apiGet }) => {
      apiGet(`/adriva_api/dq/token-map/${encodeURIComponent(entityId)}`)
        .then((res: any) => { if (res?.success) this.setState({ tokenMap: res.data }); })
        .catch(() => {});
    });
  }

  // ─── self-fetch: DM + profile (+ rules), 50 cols per sub-entity, one call ───

  _loadDMProfile(entityId: string) {
    const { appNm } = this.props;
    if (!appNm || !entityId) return;
    this.setState({ dmLoading: true });

    getDMProfile(appNm, entityId, LAZY_CONFIG.PAGE_SIZE)
      .then((res: DMProfile) => {
        const subMeta: State['subMeta'] = {};
        const loadedFields: Record<string, any[]> = {};
        let rootName = '';
        (res.sub_entities || []).forEach((s) => {
          subMeta[s.name] = {
            total:   s.total,
            loaded:  s.loaded,
            hasMore: s.has_more,
            loading: false,
            isRoot:  s.is_root,
            label:   s.label,
          };
          loadedFields[s.name] = s.fields || [];
          if (s.is_root) rootName = s.name;
        });

        const entry = this._assembleEntry(res.entity_label, res.sub_entities, loadedFields, rootName);
        const groups = this._extractGroupsFromDm(entry);
        this.setState({
          dmLocal:     entry,
          subMeta,
          entityLabel: res.entity_label,
          dmLoading:   false,
          activeGroup: groups.length ? groups[0].label : '',
        });
      })
      .catch(() => this.setState({ dmLoading: false }));
  }

  // load more fields for ONE sub-entity (the "Load more" action)
  _loadMore(subName: string) {
    const { appNm, entityId } = this.props;
    const meta = this.state.subMeta[subName];
    if (!appNm || !entityId || !meta || !meta.hasMore || meta.loading) return;

    this.setState(prev => ({
      subMeta: { ...prev.subMeta, [subName]: { ...prev.subMeta[subName], loading: true } },
    }));

    getDMProfilePage(appNm, entityId, subName, meta.loaded, LAZY_CONFIG.PAGE_SIZE)
      .then((res: DMProfilePage) => {
        this.setState(prev => {
          // append to the matching group / root in dmLocal
          const dm = prev.dmLocal ? JSON.parse(JSON.stringify(prev.dmLocal)) : null;
          if (dm) {
            if (prev.subMeta[subName]?.isRoot) {
              const leaves = (dm.field || []).filter((f: any) => f.eClass !== 'FieldGroup');
              const groups = (dm.field || []).filter((f: any) => f.eClass === 'FieldGroup');
              dm.field = [...leaves, ...res.fields, ...groups];
            } else {
              (dm.field || []).forEach((g: any) => {
                if (g.eClass === 'FieldGroup' && g.name === subName) {
                  g.field = [...(g.field || []), ...res.fields];
                  g._lazyLoaded  = (g.field || []).length;
                  g._lazyTotal   = res.total;
                  g._lazyHasMore = res.has_more;
                }
              });
            }
          }
          return {
            dmLocal: dm,
            subMeta: {
              ...prev.subMeta,
              [subName]: {
                ...prev.subMeta[subName],
                loaded:  prev.subMeta[subName].loaded + (res.fields?.length || 0),
                total:   res.total,
                hasMore: res.has_more,
                loading: false,
              },
            },
          };
        });
      })
      .catch(() => {
        this.setState(prev => ({
          subMeta: { ...prev.subMeta, [subName]: { ...prev.subMeta[subName], loading: false } },
        }));
      });
  }

  // assemble a refinedDM-shaped entry from the dm-profile response
  _assembleEntry(entityLabel: string, subs: any[], loadedFields: Record<string, any[]>, rootName: string) {
    const base = this.props.dm || {};
    const rootLeaves = loadedFields[rootName] ?? [];
    const groups = (subs || [])
      .filter((s: any) => !s.is_root)
      .map((s: any) => ({
        eClass:       'FieldGroup',
        id:           `__grp__${s.name}`,
        name:         s.name,
        label:        s.label,
        field:        loadedFields[s.name] ?? [],
        _lazyTotal:   s.total,
        _lazyLoaded:  (loadedFields[s.name] ?? []).length,
        _lazyHasMore: s.has_more,
      }));
    return {
      ...base,
      label: entityLabel || base.label,
      name:  rootName || base.name,
      field: [...rootLeaves, ...groups],
    };
  }


  // ─── samples on demand ─────────────────────────────────────────────────────
  // Keyed by source_prefix (matches the backend /lazy/samples filter).

  _requestSamples(sourcePrefix: string) {
    if (this.state.samplesCache[sourcePrefix] || this.state.samplesLoading[sourcePrefix]) return;
    this.setState(prev => ({ samplesLoading: { ...prev.samplesLoading, [sourcePrefix]: true } }));
    getSamples(this.props.entityId, sourcePrefix)
      .then(res => {
        this.setState(prev => ({
          samplesCache:   { ...prev.samplesCache,   [sourcePrefix]: res.samples },
          samplesLoading: { ...prev.samplesLoading, [sourcePrefix]: false },
        }));
      })
      .catch(() => {
        this.setState(prev => ({ samplesLoading: { ...prev.samplesLoading, [sourcePrefix]: false } }));
      });
  }

  // ─── group helpers ──────────────────────────────────────────────────────────

  _extractGroupsFromDm(dm: any) {
    const groups: { name: string; label: string; fields: any[] }[] = [];
    const ungrouped: any[] = [];
    (dm?.field || []).forEach((f: any) => {
      if (f.eClass === 'FieldGroup') groups.push({ name: f.name || f.label || 'Unnamed', label: f.label || f.name || 'Unnamed', fields: f.field || [] });
      else ungrouped.push(f);
    });
    if (ungrouped.length) groups.unshift({ name: dm?.name || dm?.label || 'Root', label: dm?.label || 'Root', fields: ungrouped });
    return groups;
  }

  extractGroups() {
    return this._extractGroupsFromDm(this.state.dmLocal ?? this.props.dm);
  }

  // collect all loaded validation rules across the (loaded) fields
  _allLoadedRules(): any[] {
    const out: any[] = [];
    const walk = (fields: any[]) => (fields || []).forEach((f: any) => {
      if (f.eClass === 'FieldGroup') { walk(f.field || []); return; }
      (f?.profile?.insight?.validation_rules ?? []).forEach((r: any) =>
        out.push({ ...r, lifecycle: resolveLifecycle(r.lifecycle) }));
    });
    walk(this.state.dmLocal?.field || []);
    return out;
  }

  // ─── dashboard counts ───────────────────────────────────────────────────────

  buildDashboardCounts() {
    const { dqSummary, ruleStatusFilter } = this.state;

    // rules from the fields loaded so far (for category/dimension facets)
    const allRules = this._allLoadedRules();

    // Pill counts: prefer the server summary (covers ALL rules, even unloaded pages);
    // fall back to loaded rules if the summary isn't in yet.
    let counts: Record<string, number>;
    let totalRules: number;
    if (dqSummary) {
      const { by_lifecycle, total } = dqSummary;
      counts = {
        ALL:      total,
        Approved: by_lifecycle['Approved'] ?? 0,
        Draft:    by_lifecycle['Draft']    ?? 0,
        Rejected: by_lifecycle['Rejected'] ?? 0,
        Waiting:  by_lifecycle['Waiting']  ?? 0,
      };
      totalRules = total;
    } else {
      counts = {
        ALL:      allRules.length,
        Approved: allRules.filter((r: any) => r.lifecycle === 'Approved').length,
        Draft:    allRules.filter((r: any) => r.lifecycle === 'Draft').length,
        Rejected: allRules.filter((r: any) => r.lifecycle === 'Rejected').length,
        Waiting:  allRules.filter((r: any) => r.lifecycle === 'Waiting').length,
      };
      totalRules = allRules.length;
    }

  const filtered = allRules.filter((r: any) => {
     if (ruleStatusFilter !== 'ALL' && r.lifecycle !== ruleStatusFilter) return false;
     if (this.state.catFilter && r.cat !== this.state.catFilter) return false;
     if (this.state.dimFilter && r.dq_dim !== this.state.dimFilter) return false;
     return true;
   });
   const catCounts = allRules
     .filter((r: any) => ruleStatusFilter === 'ALL' || r.lifecycle === ruleStatusFilter)
     .reduce((a: any, r: any) => { if (r.cat) a[r.cat] = (a[r.cat]||0)+1; return a; }, {});
   const dimCounts = allRules
     .filter((r: any) => ruleStatusFilter === 'ALL' || r.lifecycle === ruleStatusFilter)
     .reduce((a: any, r: any) => { if (r.dq_dim) a[r.dq_dim] = (a[r.dq_dim]||0)+1; return a; }, {});
   return { counts, catCounts, dimCounts, totalRules, allRules, filtered };
  }

  // ─── rule actions (unchanged) ───────────────────────────────────────────────

  openRuleModal  = (field: any, rule: any) => this.setState({ selectedField: field, selectedRule: rule });
  closeRuleModal = () => this.setState({ selectedField: null, selectedRule: null });

  toggleGroupCollapse = (label: string) => {
    this.setState(prev => {
      const eg = new Set(prev.expandedGroups);
      eg.has(label) ? eg.delete(label) : eg.add(label);
      return { expandedGroups: eg };
    });
  };

  handleDQRuleUpdateLifeCycle = (rule: any, lifecycle: string) => {
    const payload = {
      action: 'DQ_RULE_UPDATE_LIFE_CYCLE', app_nm: this.props.selectedApp?.nm,
      entity_id: this.props.entityId,
      data: { rule_id: rule.id, rule_column_name: rule.field, rule_source_prefix: this.state.selectedField?.profile?.source_prefix, rule_life_cycle: lifecycle },
    };
    adriva_dq_postAction(JSON.stringify(payload), (res: any) => {
      if (res.success) {
        this.setState(prev => {
          const dm = JSON.parse(JSON.stringify(prev.dmLocal));
          const walk = (fields: any[]) => fields.forEach((f: any) => {
            if (f.eClass === 'FieldGroup') walk(f.field || []);
            else (f?.profile?.insight?.validation_rules || []).forEach((r: any) => { if (r.id === rule.id) r.lifecycle = lifecycle; });
          });
          walk(dm.field || []);
          // also update dqSummary counts optimistically
          const dqSummary = prev.dqSummary ? { ...prev.dqSummary } : null;
          if (dqSummary) {
            const oldLc = resolveLifecycle(rule.lifecycle);
            dqSummary.by_lifecycle = { ...dqSummary.by_lifecycle };
            dqSummary.by_lifecycle[oldLc]  = Math.max(0, (dqSummary.by_lifecycle[oldLc]  || 0) - 1);
            dqSummary.by_lifecycle[lifecycle] = (dqSummary.by_lifecycle[lifecycle] || 0) + 1;
          }
          return { dmLocal: dm, dqSummary };
        });
        this.closeRuleModal();
      } else alert('Failed to update rule');
    }, () => alert('Failed to update rule'));
  };

  // Add Rule — from the "Add Rule" buttons on a field row
  handleAddDQRulesTuningTrigger = (field: any) => {
    const p = field?.profile || field;
    this.setState({
      selectedField: { profile: p },
      studio: {
        mode:        'add',
        sourcePrefix: p?.source_prefix,
        column:       p?.orig_column_name || p?.column_name,
        columnLabel:  p?.column_name,
        existingRule: null,
      },
    });
  };

  // Tune Rule — from inside the rule details modal ("Tune" action)
  handleDQRuleTuningTrigger = () => {
    const p = this.state.selectedField?.profile;
    const rule = this.state.selectedRule;
    if (!p || !rule) return;
    this.setState({
      studio: {
        mode:        'tune',
        sourcePrefix: p?.source_prefix,
        column:       p?.orig_column_name || p?.column_name,
        columnLabel:  p?.column_name,
        existingRule: rule,
      },
    });
  };

  applyRuleAcceptLocally = (acceptedRule: any) => {
    this.setState(prev => {
      const dm = JSON.parse(JSON.stringify(prev.dmLocal));
      const walk = (fields: any[]) => fields.forEach((f: any) => {
        if (f.eClass === 'FieldGroup') walk(f.field || []);
        else {
          const rules = f?.profile?.insight?.validation_rules || (f.profile = f.profile || {}, f.profile.insight = f.profile.insight || {}, f.profile.insight.validation_rules = []);
          let found = false;
          rules.forEach((r: any, i: number) => { if (r.id === acceptedRule.id) { rules[i] = acceptedRule; found = true; } });
          if (!found && f.field === acceptedRule.field) rules.push(acceptedRule);
        }
      });
      walk(dm.field || []);
      return { dmLocal: dm };
    });
  };

  handleRuleAccept = (acceptedRule: GeneratedRule | any, validationResult?: any) => {
    const s = this.state.studio;
    const payload = {
      action: 'DQ_RULE_ACCEPT',
      app_nm: this.props.selectedApp?.nm,
      entity_id: this.props.entityId,
      data: {
        rule:          acceptedRule,
        column_name:   s?.column,
        source_prefix: s?.sourcePrefix,
        is_tune:           s?.mode === "tune",   
        existing_rule_id:  s?.mode === "tune" ? (s?.existingRule?.id ?? null) : null,  
        validation_result: validationResult,
      },
    };
    adriva_dq_postAction(JSON.stringify(payload), (res: any) => {
      if (res.success) {
        // normalise field[] so applyRuleAcceptLocally can match the column
        this.applyRuleAcceptLocally(acceptedRule);
      } else {
        alert('Failed to accept rule');
      }
    }, () => alert('Failed to accept rule'));
    // NOTE: the studio stays open so the user can accept multiple generated
    // rules; it closes via its own Close button (onHide below).
  };


  // ─── renderRulesPanel ───────────────────────────────────────────────────────

  renderRulesPanel = (field: any, insight: any) => {
    const { ruleStatusFilter, catFilter, dimFilter, sourceFilter, cleansingFilters, tokenMap } = this.state;
    const resultMap: Record<string, any> = {};
    (insight.validation_results || []).forEach((r: any) => { resultMap[r.rule_id] = r; });
    const merged = (insight.validation_rules || []).map((r: any) => ({
      ...r, lifecycle: resolveLifecycle(r.lifecycle), result: resultMap[r.id] || {},
    }));
    const blocksForCol = getBlocksForField(tokenMap, field);
    const visible = merged.filter((r: any) => {
      if (ruleStatusFilter !== 'ALL' && r.lifecycle !== ruleStatusFilter) return false;
      if (catFilter && r.cat !== catFilter) return false;
      if (dimFilter && r.dq_dim !== dimFilter) return false;
      if (sourceFilter === 'policy' && !r.policy_context?.file_name) return false;
      if (sourceFilter === 'llm'    &&  r.policy_context?.file_name) return false;
      if (cleansingFilters.transformKind || cleansingFilters.tokenType || cleansingFilters.editability) {
        if (r.cat !== 'std') return false;
        const blk = blocksForCol[r.id];
        if (!blk) return false;
        if (cleansingFilters.tokenType && blk.token_type !== cleansingFilters.tokenType) return false;
        if (cleansingFilters.transformKind && deriveKind(blk) !== cleansingFilters.transformKind) return false;
        if (cleansingFilters.editability) {
          const isEng = ENGINEER_KINDS.has(deriveKind(blk));
          if (cleansingFilters.editability === 'editable' && isEng) return false;
          if (cleansingFilters.editability === 'engineer' && !isEng) return false;
        }
      }
      return true;
    });
    const catGroups: Record<string, any[]> = {};
    visible.forEach((r: any) => { const k = r.cat || 'other'; if (!catGroups[k]) catGroups[k] = []; catGroups[k].push(r); });

    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:T.textMuted }}>
            Rules ({visible.length})
          </span>
          <button className="add-rule-btn" onClick={() => this.handleAddDQRulesTuningTrigger(field)}>
            <FontAwesomeIcon icon={faPlus} style={{ fontSize:10 }} /> Add Rule
          </button>
        </div>
        {visible.length === 0 && (
          <div style={{ fontSize:12, color:T.textMuted, padding:'12px 0' }}>
            No rules match current filters.
            {sourceFilter !== 'ALL' && (
              <button onClick={() => this.setState({ sourceFilter: 'ALL' })} style={{ marginLeft:8, fontSize:11, color:T.brand, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
                Clear source filter
              </button>
            )}
          </div>
        )}
        {Object.keys(catGroups).map(cat => (
          <div key={cat} style={{ marginBottom:14 }}>
            <div className="rules-cat-hdr">{CAT_LABELS[cat] || cat}</div>
            {catGroups[cat].map((rule) => {
              const lcKey = rule.lifecycle as keyof typeof LC_ICON;
              const icon  = LC_ICON[lcKey] || faPen;
              const iconColor = lcKey === 'Approved' ? T.green : lcKey === 'Rejected' ? T.red : lcKey === 'Waiting' ? T.amber : T.textMuted;
              return (
                <div key={rule.id} className="rule-row">
                  <FontAwesomeIcon icon={icon} style={{ fontSize:13, color:iconColor, marginTop:2, flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, fontWeight:700, color:T.brand, cursor:'pointer', textDecoration:'underline dotted' }} onClick={() => this.openRuleModal(field, rule)}>
                        {rule.id}
                      </span>
                      <span className={`lc-badge lc-${rule.lifecycle}`}>{rule.lifecycle}</span>
                      {rule.dq_dim && (
                        <span style={{ fontSize:10, fontWeight:600, color:DIM_COLOR[rule.dq_dim]||T.textMuted, background:`${DIM_COLOR[rule.dq_dim]||T.textMuted}18`, borderRadius:8, padding:'1px 7px' }}>
                          {rule.dq_dim}
                        </span>
                      )}
                      {rule.policy_context?.file_name
                        ? <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:8, fontWeight:700, padding:'1px 6px', borderRadius:5, background:T.brandLight, color:T.brand, border:`1px solid ${T.brandMid}` }}>policy</span>
                        : <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:8, fontWeight:700, padding:'1px 6px', borderRadius:5, background:T.bgSidebar, color:T.textMuted, border:`1px solid ${T.border}` }}>llm</span>
                      }
                    </div>
                    <div style={{ fontSize:11, color:T.textMuted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:340 }} title={rule.rule_name||rule.d}>
                      {rule.rule_name || rule.d || '—'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  renderFieldInsights = (profile: any) => {
    if (!profile?.insight?.validation_rules?.length) return null;
    const catGroups: Record<string, { approved: number; total: number; rules: any[] }> = {};
    profile.insight.validation_rules.forEach((r: any) => {
      const k = r.cat || 'other';
      if (!catGroups[k]) catGroups[k] = { approved:0, total:0, rules:[] };
      catGroups[k].total++;
      catGroups[k].rules.push(r);
      if (r.lifecycle === 'Approved') catGroups[k].approved++;
    });
    return (
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:8 }}>
        {Object.keys(catGroups).map(k => {
          const g = catGroups[k];
          const label = CAT_LABELS[k] || k;
          const all_ok = g.approved === g.total;
          return (
            <OverlayTrigger key={k} overlay={<Tooltip style={{ maxWidth:280 }}><div style={{ textAlign:'left' }}><strong>{label}</strong><ul style={{ margin:'4px 0 0', paddingLeft:16 }}>{g.rules.map((r,i) => <li key={i} style={{ fontSize:12 }}>{r.d}</li>)}</ul></div></Tooltip>} placement="top">
              <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, background:all_ok?T.greenBg:T.amberBg, color:all_ok?T.green:T.amber, border:`1px solid ${all_ok?T.greenBorder:T.amberBorder}`, cursor:'default', whiteSpace:'nowrap' }}>
                {label} {g.approved}/{g.total}
              </span>
            </OverlayTrigger>
          );
        })}
      </div>
    );
  };

  fieldHasVisibleRules = (field: any): boolean => {
    const { ruleStatusFilter, catFilter, dimFilter, sourceFilter, cleansingFilters, tokenMap } = this.state;
    const insight = field?.profile?.insight;
    if (!insight?.validation_rules?.length) return false;
    const blocksForCol = getBlocksForField(tokenMap, field);
    return (insight.validation_rules || []).some((r: any) => {
      const lc = resolveLifecycle(r.lifecycle);
      if (ruleStatusFilter !== 'ALL' && lc !== ruleStatusFilter) return false;
      if (catFilter && r.cat !== catFilter) return false;
      if (dimFilter && r.dq_dim !== dimFilter) return false;
      if (sourceFilter === 'policy' && !r.policy_context?.file_name) return false;
      if (sourceFilter === 'llm'    &&  r.policy_context?.file_name) return false;
      if (cleansingFilters.transformKind || cleansingFilters.tokenType || cleansingFilters.editability) {
        if (r.cat !== 'std') return false;
        const blk = blocksForCol[r.id];
        if (!blk) return false;
        if (cleansingFilters.tokenType && blk.token_type !== cleansingFilters.tokenType) return false;
        if (cleansingFilters.transformKind && deriveKind(blk) !== cleansingFilters.transformKind) return false;
        if (cleansingFilters.editability) {
          const isEng = ENGINEER_KINDS.has(deriveKind(blk));
          if (cleansingFilters.editability === 'editable' && isEng) return false;
          if (cleansingFilters.editability === 'engineer' && !isEng) return false;
        }
      }
      return true;
    });
  };

  renderFieldRow = (field: any) => {
    const { columnSearch, hideEmptyFields } = this.state;
    if (columnSearch) {
      const q  = columnSearch.toLowerCase();
      const nm = (field.COLUMN_NAME + ' ' + field.COLUMN_DISPLAY_NAME).toLowerCase();
      if (!nm.includes(q)) return null;
    }
    if (hideEmptyFields && !this.fieldHasVisibleRules(field)) return null;

    const profile     = field?.profile;
    const insight     = profile?.insight;
    const colId       = field.ORIG_COLUMN_NAME || field.FLD_PHYSICAL_ID?.split('-')[1] || '';
    const completeness = 100 - (profile?.null_percentage || 0);
    const uniqueness   = profile?.unique_values && profile?.count ? (profile.unique_values / profile.count) * 100 : 0;
    const fldTyp = field.DATA_TYP === 'TextField' ? `Text (${field.DATA_LEN})`
      : field.DATA_TYP === 'DecimalField' ? `Decimal (${field.DATA_LEN?.replace('|',', ')})`
      : (field.DATA_TYP || '').replace('Field','');

    return (
      <tr key={field.COLUMN_NAME}>
        <td style={{ minWidth:240, maxWidth:280 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:T.textPrimary }}>{field.COLUMN_DISPLAY_NAME}</div>
              <div style={{ fontSize:11, color:T.textMuted, marginTop:2 }}>{field.COLUMN_NAME}{colId ? ` (${colId})` : ''}</div>
              <div style={{ fontSize:11, color:T.textMuted }}>{fldTyp}</div>
            </div>
            <button onClick={() => this.props.onEditFieldClick?.(field)} style={{ background:'none', border:'none', cursor:'pointer', color:T.brandMid, padding:'2px 4px', borderRadius:4, flexShrink:0 }} title="Edit field">
              <FontAwesomeIcon icon={faPen} style={{ fontSize:12 }} />
            </button>
          </div>
          {this.props.tool === 'BUILD_DQ_USING_DATA' && (
            <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, background:completeness>90?T.greenBg:T.amberBg, color:completeness>90?T.green:T.amber, border:`1px solid ${completeness>90?T.greenBorder:T.amberBorder}` }}>C: {completeness.toFixed(0)}%</span>
              <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, background:T.purpleBg, color:T.purple, border:`1px solid ${T.purpleBorder}` }}>U: {uniqueness.toFixed(0)}%</span>
            </div>
          )}
          {this.renderFieldInsights(profile)}
        </td>
        <td style={{ minWidth:300 }}>
          {profile ? (
            <div>
              {profile.recommended_type && <div style={{ fontSize:11, fontWeight:700, color:T.brand, marginBottom:10, background:T.brandLight, borderRadius:6, padding:'3px 8px', display:'inline-block' }}>→ {profile.recommended_type}</div>}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:12, alignItems:'end' }}>
                <div><ValueTypeBars counts={profile.value_type_counts||{}} /><div className="stat-label">Value Type</div></div>
                <div><LengthBars dist={profile.data_length_distribution||[]} /><div className="stat-label">Length</div></div>
                <div><TopValueBars vals={profile.distinct_values||{}} total={profile.count||0} /><div className="stat-label">Top Values</div></div>
                <div><NullBar count={profile.count||0} nullCount={profile.null_count||0} /><div className="stat-label">Present</div></div>
              </div>
            </div>
          ) : <span style={{ fontSize:12, color:T.textMuted }}>No profile data</span>}
        </td>
        <td style={{ minWidth:280 }}>
          {insight?.validation_rules?.length
            ? this.renderRulesPanel(field, insight)
            : <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-start' }}><span style={{ fontSize:12, color:T.textMuted }}>No approved rules</span><button className="add-rule-btn" onClick={() => this.handleAddDQRulesTuningTrigger(field)}><FontAwesomeIcon icon={faPlus} style={{ fontSize:10 }} /> Add Rule</button></div>
          }
        </td>
      </tr>
    );
  };

  renderTree = (fields: any[]): JSX.Element[] =>
    fields.flatMap((field, idx) => {
      if (field.eClass === 'FieldGroup') {
        const label  = field.label || `Group ${idx}`;
        const isOpen = this.state.expandedGroups.has(label);
        return [
          <tr key={`gh-${idx}`} className="group-hdr">
            <td colSpan={3}>
              <button className="group-toggle" onClick={() => this.toggleGroupCollapse(label)}>
                <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} style={{ fontSize:11 }} />
                {label}
                <span style={{ fontSize:11, fontWeight:400, color:T.textMuted }}>({field.field?.length||0} fields)</span>
              </button>
            </td>
          </tr>,
          ...(isOpen ? this.renderTree(field.field || []) : []),
        ];
      }
      const row = this.renderFieldRow(field);
      return row ? [row] : [];
    });

  renderGroupNav = () => {
    const groups = this.extractGroups();
    const hasCleansingFilter = !!this.state.cleansingFilters.transformKind || !!this.state.cleansingFilters.tokenType || !!this.state.cleansingFilters.editability;
    const { searchAllGroups } = this.state;
    return (
      <div style={{ marginBottom:16 }}>
        <div style={{ display:'flex', overflowX:'auto', gap:6, paddingBottom:4, borderBottom:`2px solid ${T.brand}`, alignItems:'center' }}>
          {groups.map((g, i) => {
            const meta = this.state.subMeta[g.name];
            const total = meta?.total ?? g.fields.length;
            const loaded = meta?.loaded ?? g.fields.length;
            return (
              <button key={i}
                className={`dq-tab ${this.state.activeGroup === g.label && !searchAllGroups ? 'active' : ''}`}
                onClick={() => {
                  this.setState({ activeGroup: g.label, searchAllGroups: false });
                  // header-only sub-entity → load its first page on demand
                  const m = this.state.subMeta[g.name];
                  if (m && m.loaded === 0 && m.hasMore && !m.loading) this._loadMore(g.name);
                }}>
                {g.label}
                <span style={{ marginLeft:6, fontSize:10, fontWeight:700, opacity:.7 }}>
                  ({loaded < total ? `${loaded}/${total}` : total})
                </span>
                {meta?.loading && <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft:4, fontSize:9 }} />}
              </button>
            );
          })}
          {hasCleansingFilter && (
            <button onClick={() => this.setState(prev => ({ searchAllGroups: !prev.searchAllGroups }))}
              style={{ marginLeft:'auto', flexShrink:0, fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:8,
                background:searchAllGroups?T.purple:T.bgCard, color:searchAllGroups?'#fff':T.textMuted,
                border:`1px solid ${searchAllGroups?T.purple:T.border}`, cursor:'pointer', whiteSpace:'nowrap' as const, transition:'all .15s' }}>
              {searchAllGroups ? '✓ All groups' : 'Search all groups'}
            </button>
          )}
        </div>
        {searchAllGroups && hasCleansingFilter && (
          <div style={{ marginTop:6, fontSize:11, color:T.purple, background:T.purpleBg, borderRadius:6, padding:'4px 12px', display:'inline-block' }}>
            Showing matching fields across all groups —{' '}
            <button onClick={() => this.setState({ searchAllGroups: false })} style={{ fontSize:11, color:T.purple, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
              back to tab view
            </button>
          </div>
        )}
      </div>
    );
  };

  render() {
    const dm = this.state.dmLocal ?? this.props.dm;

    // dm can be null while the entity is still resolving / loading — render a
    // lightweight loading state instead of crashing on dm.label / dm.field.
    if (!dm) {
      return (
        <div className="dq-root" style={{ padding:'20px 24px', minHeight:'100vh' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:240, gap:10, color:T.textMuted }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading data quality workspace…
          </div>
        </div>
      );
    }

    const { counts, catCounts, dimCounts, totalRules, allRules, filtered } = this.buildDashboardCounts();


    const ruleIndex = this.state.dqSummary?.rule_index;
    const lcFilter  = this.state.ruleStatusFilter;
    const catF      = this.state.catFilter;
    const dimF      = this.state.dimFilter;

    let allowedRuleIds: Set<string>;
    let policyRules: any[];
    if (ruleIndex && ruleIndex.length) {
      const idx = ruleIndex.filter(r => {
        if (lcFilter !== 'ALL' && (r.lifecycle || 'Draft') !== lcFilter) return false;
        if (catF && r.cat !== catF) return false;
        // dq_dim isn't in the compact index; dim facet still applies to the
        // table + loaded rules, but the dashboards are lifecycle/cat scoped.
        return true;
      });
      allowedRuleIds = new Set<string>(idx.map(r => r.id));
      // PolicyDashboard only needs {id, source/policy_context} — shape a light
      // rule list from the index so its llm/policy split covers ALL rules.
      policyRules = idx.map(r => ({
        id: r.id,
        lifecycle: r.lifecycle,
        cat: r.cat,
        // PolicyDocDashboard checks policy_context?.file_name to decide llm/policy
        policy_context: r.source === 'policy' ? { file_name: '(policy)' } : null,
      }));
    } else {
      allowedRuleIds = new Set<string>(filtered.map((r: any) => r.id));
      policyRules = filtered;
    }

    const groups      = this.extractGroups();
    const activeGroup = groups.find(g => g.label === this.state.activeGroup);
    const hasCleansingFilter = !!this.state.cleansingFilters.transformKind || !!this.state.cleansingFilters.tokenType || !!this.state.cleansingFilters.editability;
    const anyLoading = this.state.dmLoading || this.state.summaryLoading;

    const fieldsToRender = (() => {
      if (this.state.viewMode !== 'tabs') return dm.field || [];
      if (!hasCleansingFilter)             return activeGroup?.fields || [];
      if (this.state.searchAllGroups)      return flatLeafFields(groups.flatMap(g => g.fields));
      return flatLeafFields(activeGroup?.fields || []);
    })();

    return (
      <div className="dq-root" style={{ padding:'20px 24px', minHeight:'100vh' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h4 style={{ margin:0, fontWeight:800, fontSize:20, color:T.brand, letterSpacing:'-.01em' }}>Data Quality Workspace</h4>
            <div style={{ fontSize:13, color:T.textMuted, marginTop:2 }}>{dm.label || ''}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {/* ── Cross-entity rules button ── */}
            <button
              onClick={() => this.setState({ showCrossEntity: true })}
              style={{
                display:        'inline-flex',
                alignItems:     'center',
                gap:            6,
                fontSize:       12,
                fontWeight:     700,
                color:          T.purple,
                background:     T.purpleBg,
                border:         `1.5px solid ${T.purpleBorder}`,
                borderRadius:   20,
                padding:        '5px 14px',
                cursor:         'pointer',
                transition:     'all .15s',
                whiteSpace:     'nowrap' as const,
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = T.purple;
                (e.currentTarget as HTMLButtonElement).style.color      = '#fff';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = T.purpleBg;
                (e.currentTarget as HTMLButtonElement).style.color      = T.purple;
              }}
              title="View rules that span multiple sub-entities (MARA ↔ MARC ↔ MVKE)"
            >
              <FontAwesomeIcon icon={faProjectDiagram} style={{ fontSize: 11 }} />
              Cross-entity Rules
              {/* Badge: show cross-table rule count from already-fetched summary */}
              {this.state.crossEntityRuleCount != null && (
                <span style={{
                  background:   'rgba(40, 24, 24, 0.3)',
                  borderRadius: 10,
                  padding:      '1px 7px',
                  fontSize:     11,
                  fontWeight:   700,
                }}>
                  {this.state.crossEntityRuleCount}
                </span>
              )}
            </button>
            
            {/* ── Download Rules split-button ── */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => this.setState(p => ({ exportMenuOpen: !p.exportMenuOpen }))}
                disabled={this.state.exporting}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, fontWeight: 700, color: '#fff',
                  background: T.brand, border: `1.5px solid ${T.brand}`,
                  borderRadius: 20, padding: '5px 14px',
                  cursor: this.state.exporting ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap' as const, transition: 'all .15s',
                }}
                title="Download DQ rules as Excel or HTML, filtered by lifecycle"
              >
                <FontAwesomeIcon
                  icon={this.state.exporting ? faSpinner : faDownload}
                  spin={this.state.exporting}
                  style={{ fontSize: 11 }}
                />
                Download Rules
                <FontAwesomeIcon icon={faCaretDown} style={{ fontSize: 10, opacity: .8 }} />
              </button>

              {this.state.exportMenuOpen && (
                <>
                  {/* click-away backdrop */}
                  <div
                    onClick={() => this.setState({ exportMenuOpen: false })}
                    style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                  />
                  <div
                    style={{
                      position: 'absolute', top: '110%', right: 0, zIndex: 41,
                      minWidth: 460, background: T.bgCard,
                      border: `1px solid ${T.border}`, borderRadius: 12,
                      boxShadow: '0 8px 28px rgba(30,58,110,.18)', padding: 10,
                    }}
                  >
                    <div style={{
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '.06em', color: T.textMuted, padding: '4px 8px 8px',
                    }}>
                      Download DQ rules
                    </div>

                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, padding: '4px 8px 10px', borderBottom: `1px solid ${T.border}`,
                      marginBottom: 6,
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: T.textSecond }}>
                        SQL dialect
                      </span>
                      <select
                        className="dq-select"
                        value={this.state.sqlDialect}
                        onChange={e => this.setState({ sqlDialect: e.target.value as SqlDialect })}
                        style={{ fontSize: 11 }}
                      >
                        <option value="tsql">MS SQL Server</option>
                        <option value="postgres">PostgreSQL</option>
                        <option value="oracle">Oracle</option>
                      </select>
                    </div>

                    {(['Approved', 'Draft', 'Rejected', 'All'] as RuleLifecycleFilter[]).map(lc => {
                      const count =
                        lc === 'All'
                          ? this.state.dqSummary?.total
                          : this.state.dqSummary?.by_lifecycle?.[lc];
                      return (
                        <div key={lc} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: 8, padding: '6px 8px', borderRadius: 8,
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>
                            {lc}
                            {count != null && (
                              <span style={{
                                marginLeft: 6, fontSize: 11, fontWeight: 700,
                                color: T.textMuted, background: T.bgSidebar,
                                borderRadius: 10, padding: '1px 7px',
                              }}>{count}</span>
                            )}
                          </span>
                          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                            <button
                              onClick={() => this._handleExport(lc, 'xlsx')}
                              title={`Download ${lc} rules as Excel`}
                              style={exportBtnStyle(T.green, T.greenBg, T.greenBorder)}
                            >
                              <FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 11 }} /> XLSX
                            </button>
                            <button
                              onClick={() => this._handleExport(lc, 'html')}
                              title={`Download ${lc} rules as HTML`}
                              style={exportBtnStyle(T.brand, T.brandLight, T.brandMid)}
                            >
                              <FontAwesomeIcon icon={faFileCode} style={{ fontSize: 11 }} /> HTML
                            </button>
                            <button
                              onClick={() => this._handleExport(lc, 'csv')}
                              title={`Download ${lc} rules as CSV`}
                              style={exportBtnStyle(T.textSecond, T.bgSidebar, T.border)}
                            >
                              <FontAwesomeIcon icon={faFileCsv} style={{ fontSize: 11 }} /> CSV
                            </button>
                            <button
                              onClick={() => this._handleExport(lc, 'json')}
                              title={`Download ${lc} rules as JSON (with executable SQL)`}
                              style={exportBtnStyle(T.textSecond, T.bgSidebar, T.border)}
                            >
                              <FontAwesomeIcon icon={faFileCode} style={{ fontSize: 11 }} /> JSON
                            </button>
                            <button
                              onClick={() => this._handleExport(lc, 'sql', this.state.sqlDialect)}
                              title={`Download ${lc} rules as a runnable ${this.state.sqlDialect.toUpperCase()} script`}
                              style={exportBtnStyle(T.purple, T.purpleBg, T.purpleBorder)}
                            >
                              <FontAwesomeIcon icon={faDatabase} style={{ fontSize: 11 }} /> SQL
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <span style={{ fontSize:12, color:T.textMuted }}>View:</span>
            <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
              <input type="checkbox" checked={this.state.viewMode === 'tabs'}
                onChange={() => this.setState({ viewMode: this.state.viewMode === 'tree' ? 'tabs' : 'tree' })}
                style={{ accentColor:T.brand }} />
              <span style={{ fontSize:12, fontWeight:600, color:T.textSecond }}>Tabbed</span>
            </label>
          </div>
        </div>

        <div className="dq-card" style={{ padding:'14px 20px', marginBottom:20, alignItems:'center', justifyContent:'space-between' }}>
          <DQDashboard
            counts={counts} activeFilter={this.state.ruleStatusFilter}
            catFilter={this.state.catFilter} dimFilter={this.state.dimFilter}
            catCounts={catCounts} dimCounts={dimCounts} columnSearch={this.state.columnSearch}
            loading={anyLoading}
            onStatusClick={f => this.setState({ ruleStatusFilter: f, catFilter: '', dimFilter: '' })}
            onCatChange={v => this.setState({ catFilter: v })}
            onDimChange={v => this.setState({ dimFilter: v })}
            onColumnSearch={v => this.setState({ columnSearch: v })}
            onReset={() => this.setState({
              ruleStatusFilter: 'ALL', catFilter: '', dimFilter: '',
              sourceFilter: 'ALL', cleansingFilters: EMPTY_CLEANSING_FILTERS,
              hideEmptyFields: true, searchAllGroups: false,
            })}
            totalRules={totalRules}
            hideEmptyFields={this.state.hideEmptyFields}
            onToggleHideEmpty={() => this.setState(prev => ({ hideEmptyFields: !prev.hideEmptyFields }))}
          />

          <CollapsibleSection title="Cleansing Intelligence" icon={faWandMagicSparkles}
            iconBg={T.purpleBg} iconColor={T.purple}
            open={this.state.expandedDashboard === 'cleansing'}
            onToggle={() => this.setState({ expandedDashboard: this.state.expandedDashboard === 'cleansing' ? null : 'cleansing' })}
            hasActiveFilters={hasCleansingFilter}
            summary={<CleansingDashboardSummary tokenMap={this.state.tokenMap} filters={this.state.cleansingFilters} allowedRuleIds={allowedRuleIds} />}>
            <CleansingPatternDashboard tokenMap={this.state.tokenMap} filters={this.state.cleansingFilters} allowedRuleIds={allowedRuleIds}
              onFilterChange={(patch) => this.setState({ cleansingFilters: { ...this.state.cleansingFilters, ...patch } })}
              onOpenRule={(srcPrefix, col, ruleId) => {
                const findRule = (fields: any[]): { field: any; rule: any } | null => {
                  for (const f of fields) {
                    if (f.eClass === 'FieldGroup') { const hit = findRule(f.field||[]); if (hit) return hit; continue; }
                    if (f?.profile?.source_prefix !== srcPrefix) continue;
                    const fc = f?.profile?.orig_column_name || f?.profile?.column_name;
                    if (fc !== col) continue;
                    const rule = (f?.profile?.insight?.validation_rules||[]).find((r: any) => r.id === ruleId);
                    if (rule) return { field: f, rule };
                  }
                  return null;
                };
                const hit = findRule(dm.field||[]);
                if (hit) this.openRuleModal(hit.field, hit.rule);
              }} />
          </CollapsibleSection>

          <CollapsibleSection title="Policy Provenance" icon={faShieldHalved}
            iconBg={T.brandLight} iconColor={T.brand}
            open={this.state.expandedDashboard === 'policy'}
            onToggle={() => this.setState({ expandedDashboard: this.state.expandedDashboard === 'policy' ? null : 'policy' })}
            hasActiveFilters={this.state.sourceFilter !== 'ALL'}
            summary={<PolicyDashboardSummary allRules={policyRules} activeSourceFilter={this.state.sourceFilter} />}>
           <PolicyDocDashboard allRules={policyRules} activeSourceFilter={this.state.sourceFilter} onSourceFilter={v => this.setState({ sourceFilter: v })} />
          </CollapsibleSection>
        </div>

        {this.state.viewMode === 'tabs' && this.renderGroupNav()}

        <div className="dq-card" style={{ overflowX:'auto' }}>
          <table className="dq-table">
            <thead>
              <tr>
                <th style={{ minWidth:240 }}>Field</th>
                <th style={{ minWidth:300 }}>Data Profile</th>
                <th style={{ minWidth:280 }}>DQ Rules</th>
              </tr>
            </thead>
            <tbody>{this.renderTree(fieldsToRender)}</tbody>
          </table>
          {(() => {
            // "Load more columns" for the active sub-entity (tabs mode)
            if (this.state.viewMode !== 'tabs' || this.state.searchAllGroups) return null;
            const g = groups.find(gr => gr.label === this.state.activeGroup);
            const meta = g ? this.state.subMeta[g.name] : null;
            if (!meta || !meta.hasMore) return null;
            return (
              <div style={{ display:'flex', justifyContent:'center', padding:'12px' }}>
                <button onClick={() => g && this._loadMore(g.name)} disabled={meta.loading}
                  style={{ fontSize:12, fontWeight:600, color:T.brand, background:T.brandLight,
                    border:`1px solid ${T.brandMid}`, borderRadius:8, padding:'6px 16px', cursor:'pointer' }}>
                  {meta.loading
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading…</>
                    : `Load more columns — ${meta.loaded} of ${meta.total}`}
                </button>
              </div>
            );
          })()}
        </div>

        <RuleDetailsModal rule={this.state.selectedRule} show={!!this.state.selectedRule}
          onClose={this.closeRuleModal}
          onDQRuleUpdateLifeCycle={this.handleDQRuleUpdateLifeCycle}
          onDQRuleTuningTrigger={this.handleDQRuleTuningTrigger}
          entityId={this.props.entityId}
          sourcePrefix={this.state.selectedField?.profile?.source_prefix}
          column={this.state.selectedField?.profile?.orig_column_name || this.state.selectedField?.profile?.column_name}
          onTokenBlockSaved={(block: any) => { console.log('[RuleDetailsModal] token block saved:', block); }} />

        {this.state.studio && (
          <DQRuleStudio
            show={!!this.state.studio}
            onHide={() => this.setState({ studio: null })}
            entityId={this.props.entityId}
            appNm={this.props.appNm}
            sourcePrefix={this.state.studio.sourcePrefix}
            column={this.state.studio.column}
            columnLabel={this.state.studio.columnLabel}
            mode={this.state.studio.mode}
            existingRule={this.state.studio.existingRule}
            onAcceptRule={this.handleRuleAccept}
          />
        )}

        <Modal
          show={this.state.showCrossEntity}
          onHide={() => this.setState({ showCrossEntity: false })}
          size="xl"
          centered
          scrollable
          dialogClassName="cross-entity-modal"
        >
          <Modal.Header
            closeButton
            style={{
              background:   T.bgCard,
              borderBottom: `1px solid ${T.border}`,
              padding:      '14px 20px',
            }}
          >
            <Modal.Title style={{ fontSize: 15, fontWeight: 700, color: T.brand }}>
              <FontAwesomeIcon
                icon={faProjectDiagram}
                style={{ marginRight: 8, color: T.purple }}
              />
              Cross-entity Rules
              <span style={{
                marginLeft:   10,
                fontSize:     11,
                fontWeight:   600,
                color:        T.textMuted,
                background:   T.bgSidebar,
                borderRadius: 8,
                padding:      '2px 8px',
              }}>
                {dm.label || this.state.entityLabel || this.props.entityId}
              </span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              background: T.bg,
              padding:    '20px 24px',
              maxHeight:  '75vh',
              overflowY:  'auto',
            }}
          >
            {/* DQRulesReport is fully self-contained — no extra props needed */}
            <DQRulesReport
              entityId={this.props.entityId}
              style={{ fontFamily: "'Inter var','Inter',system-ui,sans-serif" }}
            />
          </Modal.Body>

          <Modal.Footer
            style={{
              background:  T.bgCard,
              borderTop:   `1px solid ${T.border}`,
              padding:     '10px 20px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              onClick={() => this.setState({ showCrossEntity: false })}
              style={{
                fontSize:     12,
                fontWeight:   600,
                color:        T.textMuted,
                background:   T.bgSidebar,
                border:       `1px solid ${T.border}`,
                borderRadius: 8,
                padding:      '6px 16px',
                cursor:       'pointer',
              }}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>

        
      </div>
    );
  }
}